import React, { useMemo } from 'react';
import { ParagraphSmall } from 'baseui/typography';
import {
  Button,
  defaultBrandingData,
  Icon,
  Input,
  StyleProvider,
} from '@relaynetwork/design-system';
import { Block } from 'baseui/block';
import { ChatStamp } from '@app/two-way/relay-messenger/components/chat-stamp/ChatStamp';
import { Card as BaseCard } from 'baseui/card';

type TwoWayPreviewProps = {
  brandingColor: string;
  autoResponseMessage: string;
  isTranslationsEnabled?: boolean;
};

export type TwoWayChatMessage = {
  id?: string;
  author: string;
  timestamp: string;
  body: string;
  displayName: string;
};

const ChatText = () => {
  return (
    <div style={{ flex: 1 }}>
      <Input
        placeholder="Type your message..."
        type="text"
        aria-label="message-input"
        data-testid="two-way-chat-input"
        size="small"
      />
    </div>
  );
};

function TwoWayPreview({
  brandingColor,
  autoResponseMessage,
  isTranslationsEnabled = false,
}: TwoWayPreviewProps) {
  const getFormattedDate = () => {
    const date = new Date();
    return date.toISOString();
  };

  const brandingData = useMemo(
    () => ({
      ...defaultBrandingData,
      color: brandingColor,
    }),
    [brandingColor],
  );

  return (
    <StyleProvider brandingData={brandingData}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '540px',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          backgroundColor: '#fff',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '50px',
            alignItems: 'center',
            justifyContent: 'right',
            marginRight: '1rem',
          }}
        >
          <Icon
            name="Delete"
            title="Delete"
            color="#231F20"
            data-testid="modal-close"
            size="small"
          />
        </div>
        <main
          style={{
            overflowY: 'auto',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              maxWidth: '75%',
              marginTop: '1rem',
              marginBottom: '1rem',
              marginRight: 'auto',
              paddingLeft: '1rem',
              paddingRight: '1rem',
            }}
          >
            <BaseCard
              overrides={{
                Root: {
                  style: {
                    borderTopLeftRadius: '1.25rem',
                    borderTopRightRadius: '1.25rem',
                    borderBottomLeftRadius: '1.25rem',
                    borderBottomRightRadius: '1.25rem',
                    borderTopColor: '#EDEEEF',
                    borderRightColor: '#EDEEEF',
                    borderBottomColor: '#EDEEEF',
                    borderLeftColor: '#EDEEEF',
                    backgroundColor: '#edeeef',
                  },
                },
                Contents: {
                  style: {
                    marginTop: '0px',
                    marginBottom: '0px',
                    marginLeft: '0px',
                    marginRight: '0px',
                    paddingTop: '1rem',
                    paddingBottom: '1rem',
                    paddingLeft: '1rem',
                    paddingRight: '1rem',
                  },
                },
                Body: {
                  style: {
                    marginBottom: '0px',
                  },
                },
              }}
            >
              <div
                style={{
                  marginBottom: '0px',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: '#000000',
                }}
              >
                <div>{autoResponseMessage}</div>
              </div>
            </BaseCard>
            <Block
              overrides={{
                Block: {
                  style: () => ({
                    paddingLeft: '1.5rem',
                    marginTop: '0.5rem',
                  }),
                },
              }}
            >
              <ChatStamp
                user="system"
                timestamp={getFormattedDate()}
                variant="user-time"
              ></ChatStamp>
            </Block>
          </div>
          <div
            style={{
              marginTop: 'auto',
            }}
          >
            {/* 
            multilingual
            */}
            {isTranslationsEnabled && (
              <div
                style={{
                  textAlign: 'right',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexDirection: 'row',
                  paddingRight: '16px',
                  color: brandingData.color,
                }}
              >
                <svg
                  width={20}
                  height={20}
                  viewBox="0 0 24 24"
                  role="img"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3 a9 9 0 1 0 0 18 a9 9 0 1 0 0 -18 M3 12 H21 M12 3 a4 9 0 1 0 0 18 a4 9 0 1 0 0 -18"
                    stroke={brandingData.color}
                    strokeWidth={2}
                    fill="none"
                  />
                </svg>
                <ParagraphSmall
                  overrides={{
                    Block: {
                      style: {
                        marginLeft: '4px',
                        marginBottom: '0px',
                        marginTop: '0px',
                        cursor: 'pointer',
                        color: brandingData.color,
                      },
                    },
                  }}
                >
                  Change Language
                </ParagraphSmall>
              </div>
            )}
            {/* footer */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                gap: '0.5rem',
                backgroundColor: '#edeeef',
              }}
            >
              <ChatText />
              <Button
                type="submit"
                shape="circle"
                variant="primary"
                aria-label="send-button"
                size="small"
              >
                <Icon name="Send" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </StyleProvider>
  );
}

export default TwoWayPreview;
